import { MultipleChoiceData } from "../../Helpers/Functions/universalSchema"
import { Map } from "immutable"
import { APIJobModel } from "./JobModel"
import { fromNow } from "../../Helpers/Functions/dateFormat"
import { FeedbackTagStats } from "./AppInstanceModel"
import { UITaskModel } from "./TaskModel"

interface FeedbackOption {
    tag: string
    value: string
    text?: string
    options?: FeedbackOption[]
}

interface NewFeedbackModel {
    choices: FeedbackOption[]
    selections?: FeedbackOption[]
}

interface OldFeedbackModel {
    choices?: MultipleChoiceData
    text: string
}
export type JobFeedbackModel = NewFeedbackModel | OldFeedbackModel

export enum FeedbackChoices {
    NOISY = "noisy",
    DIFFICULT = "difficult",
    NOT_UNDERSTAND = "not_understand",
    ERROR = "error",
    NOT_APPLICABLE = "not_applicable",
    NOT_SURE = "not_sure",
    INPUT_DATA_PROBLEM = "input_data_problem",
    TOO_LONG = "too_long",
    LABELING_PROBLEM = "labeling_problem",
    SOMETHING_ELSE = "something_else",
    NOT_SURE_NEW = "Task unclear",
    INPUT_DATA_PROBLEM_NEW = "Input problem",
    TOO_LONG_NEW = "Duration problem",
    LABELING_PROBLEM_NEW = "Tool problem",
    SOMETHING_ELSE_NEW = "Other"
}

export enum FeedbackMessageState {
    ACTIVE = "ACTIVE",
    DELETED = "DELETED",
    FLAGGED = "FLAGGED"
}

export enum FeedbackMessageViewState {
    UNREAD = "UNREAD",
    READ = "READ",
    ACKNOWLEDGED = "ACKNOWLEDGED"
}

export enum FeedbackOptionTextField {
    NONE = "NONE",
    OPTIONAL = "OPTIONAL",
    REQUIRED = "REQUIRED"
}

export interface APIFeedbackTag {
    id: number
    parentId?: number
    tag: string
    active: boolean
    position: number
    created: string
    updated: string
    checkbox: boolean
    textField: FeedbackOptionTextField
    editable: boolean
    value: string
}

export enum FeedbackMessageCreatorType {
    OWNER = "owner",
    COLLABORATOR_LABELER = "collab",
    WORKER = "worker"
}
export interface APIFeedbackMessage {
    id: string
    feedbackConversationId: string
    correct: boolean
    message: string
    created: string
    updated: string
    response: any
    state: FeedbackMessageState
    ownerId?: number
    ownerEmail?: string
    labelerId?: number
    labelerEmail?: string
    workerId?: string
    tag?: APIFeedbackTag
    viewState?: FeedbackMessageViewState
    taskId?: string
    labelerTaskId?: string
}

export interface JobFeedbackConversation {
    jobId: string
    owners: { id: string }[]
    workers: { id: string }[]
    task: { id: string }[]
    feedbackTags: string[]
    messages: APIFeedbackMessage[]
}

export const getWorkerId = (workerId: string) => workerId.toString().slice(-5)

export const hidden_feedback_tag = "instructions_don't_cover_this_case"
export type GroupedMessages = {
    [messageCreatorId: string]: APIFeedbackMessage[]
}

enum FeedbackDownloadType {
    FEEDBACK = "feedback",
    RESPONSE = "response"
}

enum FeedbackDownloadSenderType {
    LABELER = "labeler",
    REVIEWER = "reviewer"
}

type FeedbackMessageDownload = {
    type: FeedbackDownloadType
    sender?: string
    sender_type: FeedbackDownloadSenderType
    body: string
    created: string
}

type FeedbackDownload = {
    type: FeedbackDownloadType
    sender?: string
    sender_type: FeedbackDownloadSenderType
    tag?: string
    tagId?: number
    value?: string
    created: string
    suboptions?: { tag?: string; tagId?: number; value?: string }[]
}

/**
 *
 * If the messages is feedback, it should contain atleast one tagged message
 */
export function isFeedback(messages?: APIFeedbackMessage[]) {
    return !!messages?.find(message => !!message.tag)
}

export function decodeFeedbackChoice(choice: string): FeedbackChoices {
    switch (choice) {
        case FeedbackChoices.DIFFICULT:
            return FeedbackChoices.DIFFICULT
        case FeedbackChoices.ERROR:
            return FeedbackChoices.ERROR
        case FeedbackChoices.NOISY:
            return FeedbackChoices.NOISY
        case FeedbackChoices.NOT_UNDERSTAND:
            return FeedbackChoices.NOT_UNDERSTAND
        case FeedbackChoices.NOT_SURE:
            return FeedbackChoices.NOT_SURE
        case FeedbackChoices.INPUT_DATA_PROBLEM:
            return FeedbackChoices.INPUT_DATA_PROBLEM
        case FeedbackChoices.TOO_LONG:
            return FeedbackChoices.TOO_LONG
        case FeedbackChoices.LABELING_PROBLEM:
            return FeedbackChoices.LABELING_PROBLEM
        case FeedbackChoices.SOMETHING_ELSE:
            return FeedbackChoices.SOMETHING_ELSE
        case FeedbackChoices.NOT_SURE_NEW:
            return FeedbackChoices.NOT_SURE_NEW
        case FeedbackChoices.INPUT_DATA_PROBLEM_NEW:
            return FeedbackChoices.INPUT_DATA_PROBLEM_NEW
        case FeedbackChoices.TOO_LONG_NEW:
            return FeedbackChoices.TOO_LONG_NEW
        case FeedbackChoices.LABELING_PROBLEM_NEW:
            return FeedbackChoices.LABELING_PROBLEM_NEW
        case FeedbackChoices.SOMETHING_ELSE_NEW:
            return FeedbackChoices.SOMETHING_ELSE_NEW
        default:
            return FeedbackChoices.NOT_APPLICABLE
    }
}

function transformOldFeedbackIfNecessary(
    item: JobFeedbackModel
): NewFeedbackModel {
    const oldfeedback = item as OldFeedbackModel
    const isNewFeedback = oldfeedback.choices?.choices === undefined
    if (isNewFeedback) {
        return item as NewFeedbackModel
    }

    return {
        choices: oldfeedback.choices?.choices ?? [],
        selections: oldfeedback.choices?.selections
    }
}

/**
 * Group feedback items by selected choice
 */
export function groupFeedbackItems(
    items: JobFeedbackModel[]
): Map<string, FeedbackOption[]> {
    return items.reduce((passed, item) => {
        const transformedFeedback = transformOldFeedbackIfNecessary(item)
        const selections =
            transformedFeedback.selections
                ?.filter(sel => sel.tag !== FeedbackChoices.NOT_APPLICABLE)
                .map(sel => sel) ?? []

        return selections.reduce((passed2, selection) => {
            return passed2.update(selection.tag, [], values =>
                values.concat([selection])
            )
        }, passed)
    }, Map<string, FeedbackOption[]>())
}

export const extractFeedbackItems = (
    items: JobFeedbackModel[],
    job: APIJobModel
): FeedbackDownload[] =>
    items
        .map(item => transformOldFeedbackIfNecessary(item))
        .filter(item => item.selections && item.selections.length > 0)
        .flatMap(
            item =>
                item.selections?.map(selection => ({
                    type: FeedbackDownloadType.FEEDBACK,
                    sender_type: FeedbackDownloadSenderType.LABELER,
                    tag: selection.tag,
                    value:
                        selection.options?.find(so => so.tag === "Other")
                            ?.text ?? selection.text,
                    created: job.completed,
                    suboptions: selection.options
                        ?.filter(
                            so =>
                                so.tag !== hidden_feedback_tag &&
                                so.tag !== "Other"
                        )
                        .map(so => ({
                            tag: so.tag,
                            value: so.text
                        }))
                })) ?? []
        )

export const prepareFeedbackForDownload = (
    feedbackMessages: APIFeedbackMessage[]
): FeedbackDownload[] => {
    const feedbacks = feedbackMessages.filter(message => !!message.tag)
    // parent tags should be at the top
    feedbacks.sort((fa, fb) => {
        if (fa.tag?.parentId === fb.tag?.parentId) {
            return 0
        } else if (!fa.tag?.parentId) {
            return -1
        }
        return 1
    })

    const groupedFeedbacks: {
        [id: string]: FeedbackDownload[]
    } = feedbacks.reduce((grouped, message) => {
        if (
            (!!message.workerId || !!message.labelerId) &&
            message.tag?.tag !== hidden_feedback_tag
        ) {
            if (!grouped[message.workerId ?? message.labelerId ?? ""]) {
                grouped[message.workerId ?? message.labelerId ?? ""] = []
            }
            if (!!message.tag?.parentId) {
                const parentMessages =
                    grouped[message.workerId ?? message.labelerId ?? ""]
                const parent = parentMessages.find(
                    pm => pm?.tagId === message.tag?.parentId
                )
                if (parent) {
                    if (message.tag.id <= -3 || message.tag.tag === "Other") {
                        parent.value = message.message
                    } else {
                        parent["suboptions"] = parent["suboptions"] ?? []
                        parent["suboptions"].push({
                            tag: message.tag?.tag,
                            tagId: message.tag?.id,
                            value: message.message
                        })
                    }
                }
            } else {
                grouped[message.workerId ?? message.labelerId ?? ""].push({
                    type: FeedbackDownloadType.FEEDBACK,
                    ...(message.labelerEmail
                        ? { sender: message.labelerEmail }
                        : message.workerId
                        ? { sender: getWorkerId(message.workerId) }
                        : {}),
                    sender_type: FeedbackDownloadSenderType.LABELER,
                    tag: message.tag?.tag,
                    tagId: message.tag?.id,
                    value: message.message,
                    created: message.created
                })
            }
        }
        return grouped
    }, {} as { [id: string]: FeedbackDownload[] })

    const messages = feedbackMessages.filter(message => !message.tag)
    const transformedMessages: FeedbackMessageDownload[] = messages.map(
        message => ({
            type: FeedbackDownloadType.RESPONSE,
            ...(message.ownerEmail
                ? {
                      sender: message.ownerEmail,
                      sender_type: FeedbackDownloadSenderType.REVIEWER
                  }
                : message.labelerEmail
                ? {
                      sender: message.labelerEmail,
                      sender_type: FeedbackDownloadSenderType.LABELER
                  }
                : message.workerId
                ? {
                      sender: getWorkerId(message.workerId),
                      sender_type: FeedbackDownloadSenderType.LABELER
                  }
                : { sender_type: FeedbackDownloadSenderType.LABELER }),
            body: message.message,
            created: message.created
        })
    )
    return Object.values(groupedFeedbacks).flat().concat(transformedMessages)
}

function addChatToGroupedMessages(
    groupedMessagesByCreatorId: GroupedMessages,
    creatorIdKey: string,
    message: APIFeedbackMessage
) {
    if (!groupedMessagesByCreatorId[creatorIdKey]) {
        groupedMessagesByCreatorId[creatorIdKey] = [message]
    } else {
        groupedMessagesByCreatorId[creatorIdKey].push(message)
    }
}

export function groupMessagesByCreatorId(
    messages: APIFeedbackMessage[],
    taskList?: UITaskModel[]
) {
    const groupedMessagesByCreatorId: GroupedMessages = {}

    let previousCreatorId: string | number = ""
    let previousCreatedDate = ""
    let creatorCount = 0 // for grouping in sequence messages.

    messages.forEach(message => {
        // this is a special tag which need not be rendered
        const task = taskList?.find(
            task =>
                String(task.id) === (message.taskId ?? message.labelerTaskId)
        )
        if (message.tag?.tag !== hidden_feedback_tag) {
            const creatorId =
                message.workerId ?? message.ownerId ?? message.labelerId ?? ""
            if (
                previousCreatorId === creatorId &&
                previousCreatedDate === fromNow(message.created)
            ) {
                const creatorIdKey =
                    previousCreatorId +
                    "/" +
                    (message.ownerId && message.ownerEmail
                        ? FeedbackMessageCreatorType.OWNER
                        : message.labelerId && message.labelerEmail
                        ? FeedbackMessageCreatorType.COLLABORATOR_LABELER
                        : FeedbackMessageCreatorType.WORKER) +
                    "/" +
                    (message.ownerEmail ?? message.labelerEmail ?? "Worker") +
                    "/" +
                    creatorCount +
                    "/" +
                    previousCreatedDate +
                    (task !== undefined ? `/Task ${task.taskNumber}` : "")
                addChatToGroupedMessages(
                    groupedMessagesByCreatorId,
                    creatorIdKey,
                    message
                )
            } else {
                previousCreatorId = creatorId
                previousCreatedDate = fromNow(message.created)
                creatorCount++
                const creatorIdKey =
                    previousCreatorId +
                    "/" +
                    (message.ownerId && message.ownerEmail
                        ? FeedbackMessageCreatorType.OWNER
                        : message.labelerId && message.labelerEmail
                        ? FeedbackMessageCreatorType.COLLABORATOR_LABELER
                        : FeedbackMessageCreatorType.WORKER) +
                    "/" +
                    (message.ownerEmail ?? message.labelerEmail ?? "Worker") +
                    "/" +
                    creatorCount +
                    "/" +
                    previousCreatedDate +
                    (task !== undefined ? `/Task ${task.taskNumber}` : "")
                addChatToGroupedMessages(
                    groupedMessagesByCreatorId,
                    creatorIdKey,
                    message
                )
            }
        }
    })

    return groupedMessagesByCreatorId
}

export const filterFeedbackByChildTags = (
    feedbacks: APIFeedbackMessage[]
): APIFeedbackMessage[] =>
    feedbacks
        .map((message, index, arr) => {
            if (
                message.tag &&
                message.tag.parentId &&
                message.tag.position <= -3
            ) {
                const parentMessage = arr.find(
                    parent => parent.tag?.id === message.tag?.parentId
                )
                if (parentMessage) {
                    return {
                        ...message,
                        tag: {
                            ...message.tag,
                            tag: parentMessage.tag?.tag ?? ""
                        }
                    }
                }
            }
            return message
        })
        .filter(
            // render child tag only
            (message, index, arr) =>
                !message.tag ||
                !!message.tag.parentId ||
                arr.findIndex(m => m.tag?.parentId === message?.tag?.id) === -1
        )

export const sortTagsByJobAndPosition = (
    ta: FeedbackTagStats,
    tb: FeedbackTagStats
) => {
    // sort by count and then by position
    if ((tb.jobCount ?? 0) - (ta.jobCount ?? 0) === 0) {
        if (ta.position < 0) {
            return 1
        } else if (tb.position < 0) {
            return -1
        }
        return ta.position - tb.position
    }
    return (tb.jobCount ?? 0) - (ta.jobCount ?? 0)
}

export const groupTagsByParent = (
    tags: APIFeedbackTag[]
): Map<APIFeedbackTag, APIFeedbackTag[]> =>
    tags.reduce((grouped, tag, index, arr) => {
        if (!!tag.parentId) {
            if (
                tag.tag !== "instructions_don't_cover_this_case" &&
                tag.position > -3
            ) {
                const parentTag = arr.find(pt => pt.id === tag.parentId)
                if (!!parentTag) {
                    return grouped.update(parentTag, [], values =>
                        values.concat([tag])
                    )
                }
            }
        } else {
            if (
                arr.findIndex(childTag => childTag.parentId === tag.id) ===
                    -1 ||
                arr.findIndex(
                    childTag =>
                        childTag.parentId === tag.id && childTag.position <= -3
                ) !== -1 ||
                arr.filter(
                    childTag =>
                        childTag.parentId === tag.id &&
                        childTag.position > -3 &&
                        childTag.tag !== "instructions_don't_cover_this_case"
                ).length === 0
            ) {
                return grouped.update(tag, [], values => [tag].concat(values))
            } else {
                return grouped.update(tag, [], values => values.concat([]))
            }
        }
        return grouped
    }, Map<APIFeedbackTag, APIFeedbackTag[]>())

export const sortAndFlattenGroupedTagsByPosition = (
    groupedTags: Map<APIFeedbackTag, APIFeedbackTag[]>
) => {
    const sortArray: [APIFeedbackTag, APIFeedbackTag[]][] = []
    groupedTags.forEach((value, key) => {
        sortArray.push([key, value])
    })
    sortArray.sort((a, b) => {
        if (a[0].position < 0 && b[0].position < 0) {
            return a[0].position - b[0].position
        } else if (a[0].position < 0) {
            return 1
        } else if (b[0].position < 0) {
            return -1
        }
        return a[0].position - b[0].position
    })

    return sortArray.flatMap(sortedTag => sortedTag[1])
}
